import { 
  collection, 
  query, 
  where, 
  getDocs, 
  getDoc,
  orderBy, 
  limit, 
  Timestamp,
  doc,
  updateDoc
} from 'firebase/firestore';
import { db } from '../../firebase/firebase';

/**
 * Helper to safely execute a query that might fail due to missing indexes
 */
const safeGetDocs = async (queryObj) => {
  try {
    return await getDocs(queryObj);
  } catch (error) {
    // Log warning but don't crash - likely a missing index for a specific query
    console.warn("Optional query failed (ignoring):", error.message);
    return { empty: true, docs: [] };
  }
};

/**
 * Normalize order data for consistent consumption
 */
const normalizeOrder = (doc, sellerId) => {
  const data = doc.data();
  // Handle createdAt: Firestore Timestamp or ISO string or missing
  let createdAt = new Date();
  if (data.createdAt?.toDate) {
    createdAt = data.createdAt.toDate();
  } else if (data.createdAt) {
    createdAt = new Date(data.createdAt); // Handle string/number
  }
  
  // Find products belonging to this seller (check both sellerId and shopId)
  const sellerProducts = Array.isArray(data.products) 
    ? data.products.filter(p => p.sellerId === sellerId || p.shopId === sellerId) 
    : [];
    
  // If no products for this seller (shouldn't happen if filtered correctly), use empty defaults
  const firstItem = sellerProducts.length > 0 ? sellerProducts[0] : (data.products?.[0] || {});
  
  const productImage = firstItem.productImage || data.productImage || null;
  const productTitle = firstItem.productTitle || data.productName || "Unknown Product";
  
  // Calculate total amount for THIS seller only
  let amount = 0;
  if (sellerProducts.length > 0) {
    amount = sellerProducts.reduce((sum, p) => sum + (Number(p.price || 0) * Number(p.qty || 1)), 0);
  } else {
    // Fallback for single-seller orders where products might not have sellerId or top-level is used
    amount = typeof data.productTotal === "number" ? data.productTotal :
             typeof data.amount === "number" ? data.amount : 0;
  }

  // Use product status if available (more granular), else order status
  const productStatus = firstItem.status;
  const rawStatus = productStatus || data.orderStatus || data.status || "Pending";
  const statusNormalized = rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1).toLowerCase();

  // Normalize user details
  const addressObj = data.address && typeof data.address === 'object' ? data.address : {};
  const fullName = data.fullName || data.name || addressObj.name || "Guest User";
  const phoneNumber = data.phoneNumber || data.phone || addressObj.phone || addressObj.phoneNumber || "";
  
  // Normalize address (handle various formats)
  let address = data.address;
  if (address && typeof address === 'object') {
     // If it's a map/object, try to find fullAddress or construct it
     address = address.fullAddress || address.address || "No address provided";
  } else if (typeof address !== 'string') {
     // Fallback for fullAddress at root or other fields
     address = data.fullAddress || "No address provided";
  }

  const userId = data.userId || data.buyerId || data.uid || "";

  return {
    id: doc.id,
    ...data,
    userId, // Explicitly set normalized userId
    createdAt,
    productImage,
    productTitle,
    amount: Number(amount) || 0, // Ensure number
    statusNormalized,
    sellerProducts, // Keep specific products for metrics
    fullName,
    phoneNumber,
    address: address // Override with normalized string/object
  };
};

/**
 * Fetch all orders for stats calculation
 * Uses a Triple-Query Strategy to ensure no data is missed:
 * 1. Global History (Last 1000 orders)
 * 2. Global Today (All orders from today)
 * 3. Direct Seller Match (If index exists)
 */
export const getAllOrders = async (sellerId) => {
  try {
    const ordersRef = collection(db, 'orders');
    
    // 1. Recent History Query (Increased limit)
    const historyQuery = query(
      ordersRef, 
      orderBy('createdAt', 'desc'),
      limit(1000) 
    );

    // 2. Today's Orders Query (Guarantee we don't miss today's data)
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const todayQuery = query(
      ordersRef,
      where('createdAt', '>=', startOfToday),
      orderBy('createdAt', 'desc')
    );
    
    // 3. Direct Seller Query (Try to find orders where this user is the main seller)
    // This catches cases where the order might be older than the global history limit
    // We wrap this in safeGetDocs because it might require a composite index
    const directSellerQuery = query(
      ordersRef,
      where('sellerId', '==', sellerId),
      orderBy('createdAt', 'desc'),
      limit(200)
    );
    
    // Run in parallel
    const [historySnapshot, todaySnapshot, directSnapshot] = await Promise.all([
      getDocs(historyQuery),
      getDocs(todayQuery),
      safeGetDocs(directSellerQuery)
    ]);
    
    // Merge results by ID to avoid duplicates
    const mergedDocs = new Map();
    
    // Add direct matches first (highest relevance)
    directSnapshot.docs.forEach(doc => mergedDocs.set(doc.id, doc));
    // Add today's orders
    todaySnapshot.docs.forEach(doc => mergedDocs.set(doc.id, doc));
    // Add history
    historySnapshot.docs.forEach(doc => mergedDocs.set(doc.id, doc));

    const allOrders = Array.from(mergedDocs.values()).map(doc => normalizeOrder(doc, sellerId));
    
    // Sort merged orders by date desc
    allOrders.sort((a, b) => b.createdAt - a.createdAt);

    return allOrders.filter(order => {
      // 1. Direct match at top level
      if (order.sellerId === sellerId) return true;
      
      // 2. Match inside products array (using pre-calculated sellerProducts)
      if (order.sellerProducts && order.sellerProducts.length > 0) {
        return true;
      }
      
      // 3. Fallback: Match inside raw products array just in case
      if (Array.isArray(order.products)) {
        return order.products.some(p => p.sellerId === sellerId || p.shopId === sellerId);
      }
      return false;
    });
  } catch (error) {
    console.error("Error fetching all orders:", error);
    return [];
  }
};

/**
 * Fetch recent orders for a seller
 * Reuses getAllOrders to ensure consistency and robustness
 */
export const getRecentOrders = async (sellerId, limitCount = 5) => {
  const orders = await getAllOrders(sellerId);
  return orders.slice(0, limitCount);
};

import { 
  notifyBuyerOrderConfirmed, 
  notifyBuyerOrderShipped, 
  notifyBuyerOrderDelivered,
  notifyBuyerOrderCancelled 
} from './notificationService';

/**
 * Update order status
 * @param {string} orderId 
 * @param {string} newStatus - "Pending", "Confirmed", "Shipped", "Delivered", "Cancelled", "Returned"
 * @param {string} sellerId - Required to update specific product status
 * @param {Object} trackingData - Optional { trackingId, courier, trackingUrl }
 * @param {string} buyerId - Optional, used for sending notifications
 */
export const updateOrderStatus = async (orderId, newStatus, sellerId, trackingData = null, buyerId = null) => {
  try {
    const orderRef = doc(db, 'orders', orderId);
    
    // Base update payload
    const updatePayload = {
      orderStatus: newStatus,
      status: newStatus, // Legacy field support
      updatedAt: new Date()
    };

    // Add tracking info if provided (typically for "Shipped" status)
    if (trackingData) {
      updatePayload.trackingId = trackingData.trackingId;
      updatePayload.courier = trackingData.courier;
      updatePayload.trackingUrl = trackingData.trackingUrl;
    }
    
    // In a multi-seller system, we ideally update the specific product status inside the array
    // However, for simplicity/MVP or single-seller orders, we might update the top-level status too.
    
    await updateDoc(orderRef, updatePayload);
    
    // -- Send Notification --
    // If buyerId is not provided, try to fetch it from the order document
    let targetBuyerId = buyerId;
    if (!targetBuyerId) {
      try {
        const orderSnap = await getDoc(orderRef);
        if (orderSnap.exists()) {
          const data = orderSnap.data();
          targetBuyerId = data.userId || data.uid || data.buyerId || data.customerId || data.createdBy;
        }
      } catch (err) {
        console.warn("Could not fetch order to find buyerId:", err);
      }
    }

    if (targetBuyerId) {
      // Normalize status for comparison
      const statusKey = newStatus.toLowerCase();
      
      if (statusKey === 'confirmed') {
        await notifyBuyerOrderConfirmed(orderId, targetBuyerId);
      } else if (statusKey === 'shipped' && trackingData) {
        await notifyBuyerOrderShipped(orderId, targetBuyerId, trackingData);
      } else if (statusKey === 'delivered') {
        await notifyBuyerOrderDelivered(orderId, targetBuyerId);
      } else if (statusKey === 'cancelled') {
        await notifyBuyerOrderCancelled(orderId, targetBuyerId);
      }
    } else {
      console.warn(`Skipping notification for order ${orderId}: No buyerId found`);
    }

    return { success: true };
  } catch (error) {
    console.error("Error updating order status:", error);
    throw error;
  }
};

/**
 * Calculate order metrics
 * @param {Array} orders 
 * @returns {Object} { totalOrders, totalRevenue, todayOrders, todayEarnings }
 */
export const calculateOrderMetrics = (orders) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let totalRevenue = 0;
  let todayOrdersCount = 0;
  let todayEarnings = 0;

  orders.forEach(order => {
    // amount is already calculated for this seller in normalizeOrder
    const amount = order.amount || 0;
    const status = order.statusNormalized || "Pending";

    // Revenue: exclude cancelled/refunded
    if (status !== 'Cancelled' && status !== 'Refunded') {
      totalRevenue += amount;
    }

    // Today's Stats
    const orderDate = order.createdAt;
    if (orderDate >= today) {
      todayOrdersCount++;
      // Count earnings if not cancelled
      if (status !== 'Cancelled' && status !== 'Refunded') {
        todayEarnings += amount;
      }
    }
  });

  return {
    totalOrders: orders.length,
    totalRevenue,
    todayOrdersCount,
    todayEarnings
  };
};
